import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Grid, TextField, MenuItem, Select, FormControl, InputLabel, Container, Autocomplete, AutocompleteChangeReason } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { NotificationData, NotificationTemplateStatusEnum, NotificationCommunicationTypeEnum } from '../../apis/notifications';
import notificationService from '../../services/NotificationService';
import { useSnackbar } from 'notistack';
import { NotificationTemplateDto } from '../../apis/notifications'; // Assuming the NotificationTemplateDto is imported correctly
import { useDebounce } from "../../utilities/useDebounce";
import ConfirmationDialog from '../ConfirmationDialog';
import { DropdownStyles } from './ManageCrudPage';
import { iOrganization, OrganizationContext } from '../../contexts/OrganizationContext';
import { AdminFormDataContext } from '../../contexts/AdminFormDataContext';
import NotificationInformational from './NotificationInformational';
import { NotificationSendResult } from '../../apis/notifications/models/notification-send-result';

const initialValues = {
    communication_type: NotificationCommunicationTypeEnum.Email,
    notification_template_id: null,
    auth_id: null,
    notification_schedule_id: null,
    subject: '',
    body: '',
    custom_text: '',
    is_customizable: false,
};

type NotificationFormUser = {
    auth_id: string;
    name: string;
    submission_form_id?: number;
    email_address?: string | null;
    phone_number?: string | null;
    organization: iOrganization | null | undefined;
}

type NotificationFormProps = {
    users: NotificationFormUser[];
    isOpenDialog: boolean;
    setIsOpenDialog: Dispatch<SetStateAction<boolean>>;
    allowCustomMessageTemplates: boolean;
    allowRecipientChoice?: boolean;
    hideSMS?: boolean;
    serviceHistoryUserId?: number | null;
    onClose?: () => void;
}

type NotifiedUser = {
    name: string,
    email: string | undefined
}

const NotificationForm = ({ users, isOpenDialog, setIsOpenDialog, 
    allowCustomMessageTemplates, allowRecipientChoice, hideSMS, onClose }: NotificationFormProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [templates, setTemplates] = useState<NotificationTemplateDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const { organization } = useContext(OrganizationContext);
    const { formData } = useContext(AdminFormDataContext);
    const [notificationInformationalIsOpen, setNotificationInformationalIsOpen] = useState(false);
    const [notificationSendResult, setNotificationSendResult] = useState<NotificationSendResult>({message: '', successful_notifications: [], failed_notifications: []});
    const [notifiedUsers, setNotifiedUsers] = useState<NotifiedUser[]>([]);

    const validationSchema = yup.object().shape({
        communication_type: yup.string().required('Communication type is required'),
        notification_template_id: yup.number().nullable(),
        auth_id: allowRecipientChoice ? yup.string().required('Recipient is required') : yup.string().nullable(),
        notification_schedule_id: yup.number().nullable(),
        subject: yup.string().nullable(),
        body: yup.string().required('Body is required'),
        is_customizable: yup.boolean(),
        custom_text: yup.string().when('is_customizable', {                    //custom text is required if the template is customizable
            is: true,
            then: (schema) => schema.required('Custom text is required when template is customizable'),
            otherwise: (schema) => schema.nullable(),
        }),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            let templateVariables = {};
            if(values.is_customizable) {
                templateVariables = {'Customized_Text': values.custom_text};
            }
            const notificationData: NotificationData[] = users.filter(
                user => !allowRecipientChoice || values.auth_id === user.auth_id).map(user => ({
                
                communication_type: values.communication_type,
                notification_template_id: values.notification_template_id ?? undefined,
                notification_schedule_id: values.notification_schedule_id ?? undefined,
                email_address: !user.auth_id ? user.email_address ?? undefined : undefined,
                phone_number: !user.auth_id ? user.phone_number ?? undefined : undefined,
                subject: values.subject || null,
                body: values.body,
                template_variables: templateVariables,
                auth_id: user.auth_id,
                submission_form_id: user.submission_form_id,
                ope_id: user.organization?.opeid ?? undefined,
                org_id: user.organization?.id ?? undefined,
                form_uuid: formData?.uuid ?? undefined
            }));

            try {
                const response = await notificationService.SendNotification(notificationData);
                if (response?.successful_notifications?.length > 0) {
                    enqueueSnackbar('Notification sent successfully!', { variant: "success" });
                } else {
                    enqueueSnackbar('Notification not sent', { variant: "error" });
                }
                formik.resetForm();
                setNotificationSendResult(response);

                if (formik.values.communication_type === NotificationCommunicationTypeEnum.SMS) {
                    let failedUsers: NotifiedUser[] = [];
                    response.failed_notifications.forEach(failedNotification => {
                        users.forEach(user => {
                            if (failedNotification.auth_id === user.auth_id) {
                                failedUsers.push({
                                    name: user.name,
                                    email: failedNotification.email_address
                                });
                            }
                        });
                    });
                    setNotifiedUsers(failedUsers);
                    setNotificationInformationalIsOpen(true);
                }
            } catch (error) {
                enqueueSnackbar('Error sending notification!', { variant: "error" });
            }
        },
    });

    const handleSearchTemplates = useCallback(async (inputValue: string | null = null, communication_type: string | null = null) => {
        if (loading) {
            return;
        }

        try {
            let templateResults: NotificationTemplateDto[] = [];

            if (organization?.id && formData?.uuid) {
                let searchTemplates = await notificationService.GetTemplatesByOrgId(organization.id) ?? [];
                let formTemplateLinks = await notificationService.GetFormTemplateLinks(formData.uuid) ?? [];
                if (allowCustomMessageTemplates) {
                    searchTemplates = searchTemplates.filter(t => formTemplateLinks.some(l => l.notification_template_id === t.id));
                } else {
                    searchTemplates = searchTemplates.filter(t => formTemplateLinks.some(l => l.notification_template_id === t.id && !t.is_customizable));
                }
                templateResults = [...templateResults, ...searchTemplates];
            }

            if (formik.values.communication_type) {
                templateResults = templateResults.filter(x => 
                    x.communication_type === (communication_type ?? formik.values.communication_type as string));
            }

            templateResults = templateResults.filter(t => t.status === NotificationTemplateStatusEnum.Approved);
            setTemplates(templateResults);
        } catch (error) {
            console.error('Error fetching templates', error);
            enqueueSnackbar('Error fetching templates!', { variant: 'error' });
        }
    }, [enqueueSnackbar, organization?.id, formik?.values?.communication_type, loading, formData?.uuid, allowCustomMessageTemplates]);

    useEffect(() => {
        handleSearchTemplates();
    }, [handleSearchTemplates]);

    useEffect(() => {
        if(isSending) {
            formik.handleSubmit();
            setIsSending(false);    
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSending]); //not including formik to avoid constant submissions

    const debounce = useDebounce(async (ev: any) => {
        console.log('debounce -------------------------- ');
        setLoading(true);
        await handleSearchTemplates(ev.target.value);
        setLoading(false);
    }, 500);

    const handleTemplateChange = (
        event: React.SyntheticEvent<Element, Event>,
        newValue: NotificationTemplateDto | null,
        reason: AutocompleteChangeReason
    ) => {
        formik.setFieldValue('notification_template_id', newValue?.id || null);
        if (newValue?.subject) {
            formik.setFieldValue('subject', newValue?.subject);
        }
        if (newValue?.body) {
            formik.setFieldValue('body', newValue?.body);
        }
        formik.setFieldValue('is_customizable', newValue?.is_customizable);
        
    };

    const handleCloseDialog = (isOpen: boolean) => {
        setIsOpenDialog(isOpen);
        if (!isOpen && onClose) {
            onClose();
        }
    };

    return (
        <>
        {<ConfirmationDialog
            open={isOpenDialog}
            setOpenDialog={handleCloseDialog}
            ariaPrefix={'notifications'}
            dialogTitle={'Send Notifications'}
            singleButton={false}
            ctaButtonText={'Send Notification'}
            ctaButtonWorkingText={'Sending Notification'}
            setIsWorking={setIsSending}
            isWorking={isSending}
            width={1000}
            maxWidth={1000}>

            <Container sx={{ p: { xs: 2.25, sm: 4.5 } }}>
                <form id="notificationForm" onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={DropdownStyles}>
                                <InputLabel id="communication-type-label">Communication Type</InputLabel>
                                <Select
                                    labelId="communication-type-label"
                                    id="communication_type"
                                    name="communication_type"
                                    label="Communication Type"
                                    value={formik.values.communication_type}
                                    onChange={(event) => {
                                        const { name, value } = event.target;
                                        formik.setFieldValue(name, value);
                                        formik.setFieldValue('notification_template_id', null);
                                        handleSearchTemplates(null, value);
                                    }}
                                    error={formik.touched.communication_type && Boolean(formik.errors.communication_type)}
                                >
                                    <MenuItem value={NotificationCommunicationTypeEnum.Email}>Email</MenuItem>
                                    {!hideSMS && <MenuItem value={NotificationCommunicationTypeEnum.SMS}>SMS</MenuItem>}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                id="notification_template_id"
                                options={templates}
                                getOptionLabel={(option) => option.name || ''}
                                loading={loading}
                                value={templates.find(template => template.id === formik.values.notification_template_id) || null}
                                onChange={handleTemplateChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Template"
                                        error={formik.touched.notification_template_id && Boolean(formik.errors.notification_template_id)}
                                        helperText={formik.touched.notification_template_id && formik.errors.notification_template_id}
                                        onChange={debounce}
                                    />
                                )}
                            />
                        </Grid>

                        {allowRecipientChoice && (
                            <Grid item xs={12} md={12}>
                                <FormControl fullWidth sx={DropdownStyles}>
                                    <InputLabel id="recipient-label">Recipient</InputLabel>
                                    <Select
                                        labelId="recipient-label"
                                        id="auth_id"
                                        name="auth_id"
                                        label="Recipient"
                                        value={formik.values.auth_id}
                                        onChange={formik.handleChange}
                                        error={formik.touched.auth_id && Boolean(formik.errors.auth_id)}
                                    >
                                        {users.map((user) => (
                                            <MenuItem value={user.auth_id}>{user.email_address}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {formik.values.communication_type === NotificationCommunicationTypeEnum.Email && (
                            <Grid item xs={12}>
                                <TextField
                                    id="subject"
                                    label="Subject"
                                    fullWidth
                                    disabled={true}
                                    value={formik.values.subject}
                                    onChange={formik.handleChange}
                                    error={formik.touched.subject && Boolean(formik.errors.subject)}
                                    helperText="Optional: Overrides the subject in the template"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                id="body"
                                name="body"
                                label="Body"
                                disabled={true}
                                value={formik.values.is_customizable ? formik.values.body.replace('{{Customized_Text}}',formik.values.custom_text) : formik.values.body}
                                onChange={formik.handleChange}
                                error={formik.touched.body && Boolean(formik.errors.body)}
                                helperText={formik.touched.body && formik.errors.body}
                            />
                        </Grid>
                        {formik.values.is_customizable && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    id="custom_text"
                                    name="custom_text"
                                    label="Custom Text"
                                    value={formik.values.custom_text}
                                    onChange={formik.handleChange}
                                    error={formik.touched.custom_text && Boolean(formik.errors.custom_text)}
                                    helperText={formik.touched.custom_text && formik.errors.custom_text}
                                />
                            </Grid>
                        )}
                    </Grid>
                </form>
            </Container>
        </ConfirmationDialog>}

        {notificationInformationalIsOpen &&
        <NotificationInformational
            result={notificationSendResult}
            notifiedUsers={notifiedUsers}
            isOpenDialog={notificationInformationalIsOpen}
            setIsOpenDialog={setNotificationInformationalIsOpen}
        />}
        </>
    );
}

export default NotificationForm;
